import React, { useState, useEffect } from 'react';
import ImageGrid from '../components/ImageGrid';
import ImageModal from '../components/ImageModal';
import CategoryFilter from '../components/CategoryFilter';
import { fetchCuratedImages, searchImages } from '../config/unsplash';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import useCache from '../hooks/useCache';
import { Sparkles } from 'lucide-react';

const Home = ({ addToast }) => {
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const cache = useCache(10 * 60 * 1000); // 10 min cache

  useEffect(() => {
    loadImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const loadImages = async () => {
    try {
      setIsLoading(true);
      
      const cacheKey = `home_${selectedCategory || 'curated'}_1`;
      
      // Check cache first
      const cachedData = cache.get(cacheKey);
      if (cachedData) {
        setImages(cachedData);
        setPage(2);
        setHasMore(cachedData.length === 20);
        setIsLoading(false);
        return;
      }
      
      const query = selectedCategory || 'curated';
      const response = selectedCategory
        ? await searchImages(query, 1, 20)
        : await fetchCuratedImages(1, 20);

      const imageData = response.data.results || response.data || [];
      
      // Store in cache
      cache.set(cacheKey, imageData);
      
      setImages(imageData);
      setPage(2);
      setHasMore(imageData.length === 20);
    } catch (error) {
      console.error('Error loading images:', error);
      setImages([]);
      setHasMore(false);
      addToast('Failed to load images', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreImages = async () => {
    if (!hasMore || isLoading) return;

    try {
      setIsLoading(true);
      const cacheKey = `home_${selectedCategory || 'curated'}_${page}`;
      
      // Check cache first
      const cachedData = cache.get(cacheKey);
      if (cachedData) {
        setImages((prev) => [...prev, ...cachedData]);
        setPage((prev) => prev + 1);
        setHasMore(cachedData.length === 20);
        setIsLoading(false);
        return;
      }
      
      const query = selectedCategory || 'curated';
      const response = selectedCategory
        ? await searchImages(query, page, 20)
        : await fetchCuratedImages(page, 20);

      const newImages = response.data.results || response.data || [];
      
      // Store in cache
      cache.set(cacheKey, newImages);
      
      if (newImages.length === 0) {
        setHasMore(false);
      } else {
        setImages((prev) => [...prev, ...newImages]);
        setPage((prev) => prev + 1);
      }
    } catch (error) {
      console.error('Error loading more images:', error);
      addToast('Failed to load more images', 'error');
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (image) => {
    if (!user) {
      addToast('Please login to download images', 'info');
      navigate('/login');
      return;
    }

    try {
      addToast('Downloading...', 'info');
      
      // Use Unsplash download endpoint
      const downloadUrl = `${image.urls.full}?w=1920&q=80&force=true`;
      
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pixelbazar-${image.id}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      addToast('Image downloaded successfully!', 'success');
    } catch (error) {
      console.error('Download error:', error);
      addToast('Failed to download image', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-black">
      {/* Hero/Poster Section */}
      <div className="relative h-96 sm:h-[500px] lg:h-[600px] overflow-hidden bg-gradient-to-br from-gray-950 via-gray-900 to-black">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          {images.length > 0 && (
            <img
              src={images[0]?.urls?.full}
              alt="Hero"
              className="w-full h-full object-cover opacity-30"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        </div>

        {/* Hero Content */}
        <div className="relative h-full flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-xl rounded-full border border-white/10 mb-6 hover:bg-white/10 transition">
              <Sparkles size={16} className="text-blue-400" />
              <span className="text-xs sm:text-sm font-medium text-gray-200">Discover Premium Photography</span>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight tracking-tight">
              Explore Stunning
              <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                Photography
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-base sm:text-lg text-gray-300 mb-8 max-w-2xl mx-auto font-light">
              Discover millions of high-quality images. Search, explore, and download beautiful photos for your projects.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/search')}
                className="px-8 py-3 bg-white text-black rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
              >
                Start Exploring
              </button>
              <button
                onClick={() => {
                  const element = document.getElementById('gallery');
                  element?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-8 py-3 bg-white/10 backdrop-blur-md text-white rounded-lg font-semibold border border-white/20 hover:bg-white/20 transition-all duration-300"
              >
                View Gallery
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 sm:gap-8 mt-12 pt-8 border-t border-white/10">
              <div className="hover:scale-105 transition">
                <p className="text-2xl sm:text-3xl font-bold text-white">10M+</p>
                <p className="text-xs sm:text-sm text-gray-400 font-light">Photos</p>
              </div>
              <div className="hover:scale-105 transition">
                <p className="text-2xl sm:text-3xl font-bold text-white">500K+</p>
                <p className="text-xs sm:text-sm text-gray-400 font-light">Users</p>
              </div>
              <div className="hover:scale-105 transition">
                <p className="text-2xl sm:text-3xl font-bold text-white">100%</p>
                <p className="text-xs sm:text-sm text-gray-400 font-light">Free</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
        {/* Section Header */}
        <div className="mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-3 tracking-tight">
            {selectedCategory ? `${selectedCategory} Images` : 'Curated Collection'}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 font-light">
            {selectedCategory 
              ? `Explore beautiful ${selectedCategory.toLowerCase()} photography`
              : 'Hand-picked images from our community'}
          </p>
        </div>

        {/* Category Filter */}
        <div className="mb-12">
          <CategoryFilter
            selectedCategory={selectedCategory}
            onCategoryChange={(category) => {
              setSelectedCategory(category === selectedCategory ? null : category);
            }}
          />
        </div>

        {/* Image Grid */}
        <div id="gallery">
          <ImageGrid
            images={images}
            onImageClick={(image) => {
              setSelectedImage(image);
              setIsModalOpen(true);
            }}
            onDownload={handleDownload}
            onLoadMore={loadMoreImages}
            hasMore={hasMore}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Image Modal */}
      <ImageModal
        image={selectedImage}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onDownload={handleDownload}
        addToast={addToast}
      />
    </div>
  );
};

export default Home;
